import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import styled from 'styled-components';

interface DoughnutChartProps {
  value: number; // Value between -100 and 100
  size?: number; // Diameter of the chart in pixels (default: 150)
}

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const DoughnutChart: React.FC<DoughnutChartProps> = ({ value, size = 150 }) => {
  // Ensure value stays within bounds
  const clampedValue = Math.min(100, Math.max(-100, value));
  const roundedValue = Math.round(clampedValue); // Whole number representation

  // Determine label based on value
  let label = 'Neutral';

  if (roundedValue > 67) {
    label = 'Very Positive';
  } else if (roundedValue > 33) {
    label = 'Positive';
  } else if (roundedValue > 0) {
    label = 'Slightly Positive';
  } else if (roundedValue < -66) {
    label = 'Very Negative';
  } else if (roundedValue < -33) {
    label = 'Negative';
  } else if (roundedValue < 0) {
    label = 'Slightly Negative';
  }

  // Data for the doughnut chart
  const data = {
    labels: ['Value', 'Remaining'],
    datasets: [
      {
        data: [Math.abs(clampedValue), 100 - Math.abs(clampedValue)],
        backgroundColor: [
          clampedValue >= 0 ? '#4caf50' : '#f44336', // Green for positive, red for negative
          '#ddd', // Grey for the remaining
        ],
        borderWidth: 0,
      },
    ],
  };

  // Options for the chart
  const options = {
    responsive: true,
    cutout: '70%', // Makes it a doughnut
    plugins: {
      tooltip: {
        enabled: false, // Disable tooltip
      },
      legend: {
        display: false, // Hide legend
      },
      title: {
        display: false, // Hide title
      },
    },
  };

  return (
    <Container>
      <ChartWrapper size={size}>
        <Doughnut data={data} options={options} />
        <CenterLabel>
          <Value>{roundedValue}</Value>
        </CenterLabel>
      </ChartWrapper>
      <Label>{label}</Label>
    </Container>
  );
};

export default DoughnutChart;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ChartWrapper = styled.div<{ size: number }>`
  position: relative;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
`;

const CenterLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Value = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #333;
`;

const Label = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #555;
  text-align: center;
`;
